import { KeyboardArrowDown } from "@mui/icons-material";
import { Autocomplete, Paper, TextField } from "@mui/material";
import { useEffect, useState } from "react";

import HNSColors from "../../../../ui library/HNSColors";

import CustomListBoxWithShowMore from "./CustomListBoxWithShowMore";
import useFetchDropdownData from "./hooks/useFetchDropdownData";
import { STYLES } from "./style/customStyles";

export default function SingleSelectDropdown({
  fieldName,
  fieldValue,
  isURLBased = false,
  options = [],
  url = "",
  queryParams,
  queryParamBuilder,
  customOnChange,
  disabled,
  popupIcon = <KeyboardArrowDown sx={{ fontSize: 24, color: HNSColors.primary[500] }} />,
  ListboxComponent = CustomListBoxWithShowMore,
  ListboxProps,
  disableClearable,
  error,
  label,
  placeholder,
  helperText,
  onInputChange,
  nextPageURL,
  dataFormatter,
}) {
  const [selectedValue, setSelectedValue] = useState();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const { fetchDropdownData, loading, handleOnChangeFieldValue } = useFetchDropdownData({
    setSelectedValue,
    url: nextPageURL || url,
    queryParams,
    queryParamBuilder,
    searchText,
    customOnChange,
    fieldName,
  });

  useEffect(() => {
    setSelectedValue(fieldValue ?? null);
  }, [fieldValue]);

  useEffect(() => {
    const formattedOptions = data && dataFormatter ? dataFormatter(data) : data;

    if (!Array.isArray(formattedOptions) || formattedOptions.length === 0) {
      return;
    }

    const selectedId =
      typeof fieldValue === "object" && fieldValue !== null ? fieldValue.id : fieldValue;

    if (selectedId === undefined || selectedId === null || selectedId === "") {
      return;
    }

    const matchedOption = formattedOptions.find((option) => option?.id === selectedId);

    if (matchedOption) {
      setSelectedValue(matchedOption);
    }
  }, [data, dataFormatter, fieldValue]);

  useEffect(() => {
    if (!isURLBased) {
      setData(options);
      return;
    }

    const fetchData = async () => {
      const response = await fetchDropdownData();
      setData(response || []);
    };

    fetchData();
  }, [isURLBased, options, fetchDropdownData]);

  const StyledPaper = (props) => <Paper {...props} sx={{ ...props?.sx }} />;

  return (
    <Autocomplete
      className="h-full w-full"
      size="small"
      disabled={disabled}
      options={data && dataFormatter ? dataFormatter(data) : data}
      loading={loading}
      value={selectedValue ?? null}
      popupIcon={popupIcon}
      ListboxComponent={ListboxComponent}
      ListboxProps={{ ...ListboxProps, nextPageURL }}
      PaperComponent={(props) => <StyledPaper {...props} sx={STYLES.selectMenuPaper} />}
      disableClearable={disableClearable}
      getOptionLabel={(opt) => {
        if (opt === null || opt === undefined || opt === "") {
          return "";
        }
        if (typeof opt !== "object") {
          return String(opt);
        }
        return String(opt.label ?? "");
      }}
      getOptionKey={(option) => option.id}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          label={label}
          sx={STYLES.textField}
          placeholder={placeholder}
          helperText={helperText}
        />
      )}
      onChange={(_event, newValue) => handleOnChangeFieldValue(fieldName, newValue)}
      onInputChange={(event, newInputValue, reason) => {
        if (isURLBased && reason === "input") {
          setSearchText(newInputValue ?? "");
        }
        if (isURLBased && reason === "clear") {
          setSearchText("");
        }
        onInputChange?.(event, newInputValue, reason);
      }}
    />
  );
}
